/**
 * Mobile Home Page
 *
 * Dashboard with:
 * - Hero section with "Summon ContentSage" quick action
 * - Recent activity (last 3 generated articles)
 * - Agency promo card for Ganda Tech Services
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  BookOpen,
  Database,
  ArrowRight,
  Clock,
  Rocket,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import type { MobileTabId } from './BottomTabNav';
import type { SuiteTabId } from './SuiteBottomNav';
import { ContentSageIcon } from '../ContentSageLogo';

// Union type to support both old and new navigation
type NavigationTarget = MobileTabId | SuiteTabId | 'create';

interface MobileHomePageProps {
  onNavigate: (tab: NavigationTarget) => void;
  recentPosts?: Array<{
    id: string;
    title: string;
    date: string;
    status: 'draft' | 'published' | 'processing';
  }>;
  stats?: {
    postsGenerated: number;
    wordsWritten: number;
    knowledgeDocs: number;
  };
}

export const MobileHomePage: React.FC<MobileHomePageProps> = ({
  onNavigate,
  recentPosts = [],
  stats = { postsGenerated: 0, wordsWritten: 0, knowledgeDocs: 0 },
}) => {
  const { user } = useAuth();
  const firstName = user?.displayName?.split(' ')[0] || 'there';

  return (
    <div className="px-4 py-6 space-y-6 pb-24">
      {/* Welcome Section */}
      <div className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {firstName}!
        </h1>
        <p className="text-gray-500">Ready to create something amazing?</p>
      </div>

      {/* Hero Card - Quick Action */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={() => onNavigate('create')}
        className="w-full bg-gradient-to-br from-purple-600 via-purple-700 to-purple-700 rounded-2xl p-6 text-white shadow-lg shadow-purple-200 text-left"
      >
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ContentSageIcon size={24} className="rounded-md" />
              <span className="text-sm font-medium opacity-90">Quick Action</span>
            </div>
            <h2 className="text-xl font-bold">Summon ContentSage</h2>
            <p className="text-sm opacity-80">
              Generate SEO-optimized blog posts with AI
            </p>
          </div>
          <div className="p-3 bg-white/20 rounded-xl">
            <ArrowRight className="w-6 h-6" />
          </div>
        </div>
      </motion.button>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col items-center text-center gap-1">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-lg font-bold text-gray-900">{stats.postsGenerated}</p>
            <p className="text-xs text-gray-500">Posts</p>
          </div>
        </div>
        <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col items-center text-center gap-1">
            <div className="p-2 bg-green-100 rounded-lg">
              <BookOpen className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-lg font-bold text-gray-900">
              {stats.wordsWritten > 1000
                ? `${(stats.wordsWritten / 1000).toFixed(1)}k`
                : stats.wordsWritten}
            </p>
            <p className="text-xs text-gray-500">Words</p>
          </div>
        </div>
        <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col items-center text-center gap-1">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Database className="w-4 h-4 text-purple-600" />
            </div>
            <p className="text-lg font-bold text-gray-900">{stats.knowledgeDocs}</p>
            <p className="text-xs text-gray-500">Docs</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          {recentPosts.length > 0 && (
            <button
              onClick={() => onNavigate('history')}
              className="text-sm text-purple-600 font-medium"
            >
              View All
            </button>
          )}
        </div>

        {recentPosts.length > 0 ? (
          <div className="space-y-2">
            {recentPosts.slice(0, 3).map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3"
              >
                <div className="p-2 bg-gray-100 rounded-lg">
                  <FileText className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {post.title}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500">{post.date}</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        post.status === 'published'
                          ? 'bg-green-100 text-green-700'
                          : post.status === 'processing'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {post.status}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-xl p-6 text-center">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No posts yet</p>
            <button
              onClick={() => onNavigate('create')}
              className="mt-3 text-sm text-purple-600 font-medium"
            >
              Create your first post
            </button>
          </div>
        )}
      </div>

      {/* Agency Promo Card */}
      <motion.div
        whileTap={{ scale: 0.98 }}
        className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 text-white"
      >
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/10 rounded-xl">
            <Rocket className="w-6 h-6" />
          </div>
          <div className="flex-1 space-y-2">
            <h3 className="font-bold text-lg">Need an app like this?</h3>
            <p className="text-sm text-gray-300">
              We build custom AI-powered apps for businesses. Let's bring your idea
              to life.
            </p>
            <button
              onClick={() => onNavigate('services')}
              className="mt-2 flex items-center gap-2 text-sm font-medium text-purple-400"
            >
              Contact Dev Team
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default MobileHomePage;
